import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import "../css/header.scss";
import { CartContext } from "./CartContext";

const FALLBACK_NAVIGATION = [
  { label: "Shop all", to: "/shop" },
  { label: "Spices", to: "/shop?category=spices" },
  { label: "Blends", to: "/shop?category=blends" },
  { label: "Baking", to: "/shop?category=baking" },
  { label: "Gift boxes", to: "/shop?category=gifts" },
  { label: "Our story", to: "/about" },
];

function normalizeLink(item, index) {
  if (typeof item === "string") {
    return { label: item, to: "/shop" };
  }

  const children = item?.children || item?.links || item?.items || [];

  return {
    label: item?.label || item?.title || item?.name || `Link ${index + 1}`,
    to: item?.to || item?.href || item?.path || "/shop",
    children: Array.isArray(children)
      ? children.map((child, childIndex) => normalizeLink(child, childIndex))
      : [],
  };
}

function getNavigationItems(navigation) {
  const rawItems = Array.isArray(navigation)
    ? navigation
    : navigation?.primary || navigation?.main || navigation?.categories || [];
  const items = rawItems.map((item, index) => normalizeLink(item, index));

  return items.length ? items : FALLBACK_NAVIGATION;
}

function readStoredWishlistCount() {
  if (typeof window === "undefined") return 0;

  try {
    const savedItems =
      window.localStorage.getItem("wishlistItems") ||
      window.localStorage.getItem("wishlist");
    const parsedItems = savedItems ? JSON.parse(savedItems) : [];
    return Array.isArray(parsedItems) ? parsedItems.length : 0;
  } catch {
    return 0;
  }
}

function isExternalLink(to) {
  return /^(https?:|mailto:)/i.test(to);
}

function SiteLink({ to, children, ...props }) {
  if (isExternalLink(to)) {
    return (
      <a href={to} {...props}>
        {children}
      </a>
    );
  }

  return (
    <Link to={to} {...props}>
      {children}
    </Link>
  );
}

function isLinkActive(to, location) {
  if (isExternalLink(to)) return false;

  const [pathname, search = ""] = to.split("?");
  const pathMatches =
    pathname === "/"
      ? location.pathname === "/"
      : location.pathname === pathname ||
        location.pathname.startsWith(`${pathname}/`);

  if (!pathMatches) return false;
  if (!search) return location.pathname === pathname && !location.search;

  const expectedParams = new URLSearchParams(search);
  const currentParams = new URLSearchParams(location.search);

  return Array.from(expectedParams.entries()).every(
    ([key, value]) => currentParams.get(key) === value,
  );
}

function NavigationList({ items, location, mobile = false, onNavigate }) {
  return (
    <ul
      className={
        mobile ? "header-drawer__navigation-list" : "header-navigation__list"
      }
    >
      {items.map((item) => {
        const childIsActive = item.children?.some((child) =>
          isLinkActive(child.to, location),
        );
        const active = isLinkActive(item.to, location) || childIsActive;

        return (
          <li
            className={
              mobile
                ? "header-drawer__navigation-item"
                : "header-navigation__item"
            }
            key={`${item.label}-${item.to}`}
          >
            <SiteLink
              aria-current={active ? "page" : undefined}
              className={`${
                mobile
                  ? "header-drawer__navigation-link"
                  : "header-navigation__link"
              }${active ? " is-active" : ""}`}
              onClick={onNavigate}
              to={item.to}
            >
              <span>{item.label}</span>
              {item.children?.length ? (
                <FiChevronDown aria-hidden="true" />
              ) : null}
            </SiteLink>

            {item.children?.length ? (
              <ul
                className={
                  mobile
                    ? "header-drawer__subnavigation"
                    : "header-navigation__submenu"
                }
              >
                {item.children.map((child) => {
                  const childActive = isLinkActive(child.to, location);

                  return (
                    <li key={`${child.label}-${child.to}`}>
                      <SiteLink
                        aria-current={childActive ? "page" : undefined}
                        className={childActive ? "is-active" : undefined}
                        onClick={onNavigate}
                        to={child.to}
                      >
                        {child.label}
                      </SiteLink>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function BrandMark({ compact = false, onNavigate }) {
  const brandName = siteConfig.brandName || siteConfig.shortName || "";

  return (
    <Link
      aria-label={`${brandName} home`}
      className={`header-brand${compact ? " header-brand--compact" : ""}`}
      onClick={onNavigate}
      to="/"
    >
      <span aria-hidden="true" className="header-brand__badge">
        <span className="header-brand__initial header-brand__initial--primary">
          P
        </span>
        <span className="header-brand__initial header-brand__initial--secondary">
          B
        </span>
      </span>
      <span className="header-brand__copy">
        <strong>{brandName}</strong>
        {siteConfig.tagline ? <small>{siteConfig.tagline}</small> : null}
      </span>
    </Link>
  );
}

function Header() {
  const cart = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const menuButtonRef = useRef(null);
  const drawerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [storedWishlistCount, setStoredWishlistCount] = useState(
    readStoredWishlistCount,
  );

  const navigationItems = useMemo(
    () => getNavigationItems(siteConfig.navigation),
    [],
  );

  const cartCount = (cart?.cartItems || []).reduce(
    (total, item) => total + Math.max(0, Number(item.quantity ?? 1) || 0),
    0,
  );
  const wishlistCount = Array.isArray(cart?.wishlistIds)
    ? cart.wishlistIds.length
    : storedWishlistCount;
  const routes = siteConfig.routes || {};
  const promotionText =
    siteConfig.promotion?.text ||
    siteConfig.announcement ||
    siteConfig.shipping?.message ||
    "Free shipping on orders $49+";
  const promotionLink =
    siteConfig.promotion?.link || siteConfig.promotionLink || "/shop";

  useEffect(() => {
    if (location.pathname !== "/shop") return;

    const query = new URLSearchParams(location.search).get("search") || "";
    setSearchTerm(query);
  }, [location.pathname, location.search]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const syncWishlist = () =>
      setStoredWishlistCount(readStoredWishlistCount());

    window.addEventListener("storage", syncWishlist);
    window.addEventListener("wishlistUpdated", syncWishlist);
    return () => {
      window.removeEventListener("storage", syncWishlist);
      window.removeEventListener("wishlistUpdated", syncWishlist);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    previousFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) return;

      const focusableElements = drawerRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= 960) setMenuOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      previousFocusRef.current?.focus?.();
    };
  }, [menuOpen]);

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmedTerm = searchTerm.trim();
    const search = new URLSearchParams();

    if (trimmedTerm) search.set("search", trimmedTerm);
    navigate({
      pathname: "/shop",
      search: search.toString() ? `?${search.toString()}` : "",
    });
    setMenuOpen(false);
  };

  return (
    <header className="site-header">
      <a className="site-header__skip-link" href="#main-content">
        Skip to main content
      </a>

      <div className="site-header__promotion">
        <div className="site-header__promotion-inner">
          <span>{promotionText}</span>
          <SiteLink to={promotionLink}>Shop now</SiteLink>
        </div>
      </div>

      <div className="site-header__main">
        <button
          aria-controls="site-mobile-menu"
          aria-expanded={menuOpen}
          aria-label="Open menu"
          className="header-menu-button"
          onClick={() => setMenuOpen(true)}
          ref={menuButtonRef}
          type="button"
        >
          <FiMenu aria-hidden="true" />
        </button>

        <BrandMark />

        <form
          aria-label="Site search"
          className="header-search header-search--desktop"
          onSubmit={handleSearch}
          role="search"
        >
          <label className="header-search__label" htmlFor="desktop-site-search">
            Search our pantry
          </label>
          <div className="header-search__field">
            <input
              autoComplete="off"
              id="desktop-site-search"
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={
                siteConfig.searchPlaceholder || "What are you cooking?"
              }
              type="search"
              value={searchTerm}
            />
            <button aria-label="Search" type="submit">
              <FiSearch aria-hidden="true" />
            </button>
          </div>
        </form>

        <nav aria-label="Account shortcuts" className="header-actions">
          <SiteLink
            aria-label="Account"
            className="header-action"
            to={routes.account || "/account"}
          >
            <FiUser aria-hidden="true" />
            <span>Account</span>
          </SiteLink>
          <SiteLink
            aria-label={`Wishlist${
              wishlistCount ? `, ${wishlistCount} items` : ""
            }`}
            className="header-action"
            to={routes.wishlist || "/wishlist"}
          >
            <span className="header-action__icon">
              <FiHeart aria-hidden="true" />
              {wishlistCount > 0 ? (
                <span className="header-action__count">
                  {wishlistCount > 99 ? "99+" : wishlistCount}
                </span>
              ) : null}
            </span>
            <span>Favorites</span>
          </SiteLink>
          <SiteLink
            aria-label={`Cart${cartCount ? `, ${cartCount} items` : ""}`}
            className="header-action"
            to={routes.cart || "/cartpage"}
          >
            <span className="header-action__icon">
              <FiShoppingBag aria-hidden="true" />
              {cartCount > 0 ? (
                <span className="header-action__count">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              ) : null}
            </span>
            <span>Cart</span>
          </SiteLink>
        </nav>
      </div>

      <nav
        aria-label="Primary navigation"
        className="header-navigation header-navigation--desktop"
      >
        <NavigationList items={navigationItems} location={location} />
      </nav>

      <button
        aria-label="Close menu"
        className={`header-drawer__backdrop${menuOpen ? " is-open" : ""}`}
        onClick={() => setMenuOpen(false)}
        tabIndex={menuOpen ? 0 : -1}
        type="button"
      />

      <aside
        aria-hidden={!menuOpen}
        aria-label="Mobile menu"
        className={`header-drawer${menuOpen ? " is-open" : ""}`}
        id="site-mobile-menu"
        ref={drawerRef}
      >
        <div className="header-drawer__heading">
          <BrandMark compact onNavigate={() => setMenuOpen(false)} />
          <button
            aria-label="Close menu"
            className="header-drawer__close"
            onClick={() => setMenuOpen(false)}
            ref={closeButtonRef}
            type="button"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        <form
          aria-label="Mobile site search"
          className="header-search header-search--mobile"
          onSubmit={handleSearch}
          role="search"
        >
          <label className="header-search__label" htmlFor="mobile-site-search">
            Search our pantry
          </label>
          <div className="header-search__field">
            <input
              autoComplete="off"
              id="mobile-site-search"
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={
                siteConfig.searchPlaceholder || "Search spices & gifts"
              }
              type="search"
              value={searchTerm}
            />
            <button aria-label="Search" type="submit">
              <FiSearch aria-hidden="true" />
            </button>
          </div>
        </form>

        <nav aria-label="Mobile primary navigation">
          <NavigationList
            items={navigationItems}
            location={location}
            mobile
            onNavigate={() => setMenuOpen(false)}
          />
        </nav>

        <div className="header-drawer__shortcuts">
          <SiteLink
            onClick={() => setMenuOpen(false)}
            to={routes.account || "/account"}
          >
            <FiUser aria-hidden="true" /> Account
          </SiteLink>
          <SiteLink
            onClick={() => setMenuOpen(false)}
            to={routes.wishlist || "/wishlist"}
          >
            <FiHeart aria-hidden="true" /> Favorites ({wishlistCount})
          </SiteLink>
        </div>
      </aside>
    </header>
  );
}

export default Header;
